import React, { useCallback, useState } from "react"
import styled from "@emotion/native"
import { Text } from "../Typography.tsx"
import { Modal, View } from "react-native"
import { Spacing } from "../Spacing.tsx"
import { Button3D } from "../Button3D.tsx"

type ErrorDialogRef = {
  show: (newErrorData: string, buttonAction: (() => void) | null, hideHeader: boolean) => void
}

type ErrorDialogRefObj = {
  current: ErrorDialogRef | null
}

const mainRef: ErrorDialogRefObj = {
  current: null,
}

const ErrorDialogRoot = React.forwardRef((props, ref) => {
  const [errorData, setErrorData] = useState<string | null>(null)
  const [hideHeader, setHideHeader] = useState<boolean>(false)
  const [confirmButtonAction, setConfirmButtonAction] = useState<(() => void) | null>(null)

  const show = useCallback(
    (newErrorData: string, buttonAction: (() => void) | null = null, hideHeader = false) => {
      try {
        setHideHeader(hideHeader)
        setConfirmButtonAction(buttonAction)
        const parsedErrorData = JSON.parse(newErrorData)

        if (!parsedErrorData.error.Message) {
          throw new Error("Cannot parse parsedErrorData.error.Message")
        }

        setErrorData(parsedErrorData.error.Message)
      } catch (err) {
        setErrorData("Something went wrong")
      }
    },
    []
  )

  const hide = useCallback(() => {
    setErrorData(null)
  }, [])

  React.useImperativeHandle(
    ref,
    React.useCallback(
      () => ({
        show,
        hide,
      }),
      [hide, show]
    )
  )

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={!!errorData}
      onRequestClose={() => setErrorData(null)}
    >
      <Container>
        <Content>
          <View>
            {!hideHeader && (
              <Text type="h2" color="primary">
                Oops, something is wrong
              </Text>
            )}
            <Spacing top={16} />
            <Text type="body1" color="secondary">
              {errorData}
            </Text>
          </View>
          <ButtonContainer>
            <Button3D
              type="primary2"
              title="Got it"
              onPress={() => {
                setErrorData(null)
                if (confirmButtonAction) {
                  confirmButtonAction()
                }
              }}
            />
          </ButtonContainer>
        </Content>
      </Container>
    </Modal>
  )
})

export const ErrorDialog = () => {
  const toastRef = React.useRef<ErrorDialogRef | null>(null)

  const setRef = React.useCallback((ref: ErrorDialogRef | null) => {
    if (ref) {
      toastRef.current = ref
      mainRef.current = ref
    } else {
      mainRef.current = null
    }
  }, [])

  return <ErrorDialogRoot ref={setRef} />
}

const Container = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
`

const Content = styled.View`
  background-color: white;
  border-radius: 20px;
  padding: 32px 20px 32px 20px;
  min-height: 254px;
  justify-content: space-between;
  max-width: 500px;
  margin: 0 20px;
`

const ButtonContainer = styled.View``

ErrorDialog.show = (
  errorData: string,
  buttonAction: (() => void) | null = null,
  hideHeader = false
) => {
  mainRef.current?.show(errorData, buttonAction, hideHeader)
}

ErrorDialog.showMsg = (
  errorData: string,
  buttonAction: (() => void) | null = null,
  hideHeader = false
) => {
  mainRef.current?.show(JSON.stringify({ error: { Message: errorData } }), buttonAction, hideHeader)
}
